import { Subscription } from 'rxjs';

import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorPalette } from '../../../analytics-core/interfaces/partner.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { DatatableConfigurationMapperService } from '../../../analytics-core/services/datatable-configuration.mapper.service';
import { EVENT_EMITTERS, PATHS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import * as _ from 'underscore';

export interface DeviceNetwork {
	deviceName: string;
	logoPath: string;
	impressions: number;
	percent25: any;
	value25: number;
	percent50: any;
	value50: number;
	percent75: any;
	value75: number;
	percent100: any;
	value100: number;
}

@Component({
	encapsulation: ViewEncapsulation.None,
	selector: 'app-r10-devices',
	styleUrls: ['./r10-devices.component.css'],
	templateUrl: './r10-devices.component.html'
})
export class R10DevicesComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() multichartconfigurations: any;
	@Input() multidatatableconfigurations: any;
	@Input() key: string;
	@Input() legend: any;
	@Input() sectionclass: string;
	@Input() sectiontitle: string;
	@Input() nodatamessage: any;

	public reloadSubscription: Subscription;
	public widgetDataSubscription: Subscription;
	public colorPalette: ColorPalette;
	public devicesChartData: any[] = [];
	public deviceLegendData: any[] = [];
	public showLoad = true;
	public hasData = true;
	public isCTV: boolean = false;
	public deviceCount: any;

	public deviceNetworks: any;
	public pageId: any;
	public isR1301: boolean = false;

	protected DEFAULT_DEVICES = [{ device: 'desktop' }, { device: 'mobile' }, { device: 'tablet' }, { device: 'other' }];

	constructor(
		protected analyticsDataModel: AnalyticsDataModel,
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected dataTableService: DatatableConfigurationMapperService
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
		this.isCTV = this.analyticsDataModel.getCurrentPath() === PATHS.CTV;
		if (this.isCTV) {
			this.DEFAULT_DEVICES = [{ device: 'connected tv' }];
		}
	}

	ngOnInit(): void {
		this.isR1301 = this.analyticsDataModel.config.directoryName.toLowerCase() === 'default-r1301' ? true : false;
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.multichartconfigurations = JSON.parse(decodeURI(this.multichartconfigurations));
		this.multidatatableconfigurations = JSON.parse(decodeURI(this.multidatatableconfigurations));
		if (this.multichartconfigurations) {
			this.multichartconfigurations = _.filter(this.multichartconfigurations, (item) => {
				return this.analyticsDataModel.checkComponentForPermission(item);
			});
		}
		if (this.multidatatableconfigurations) {
			this.multidatatableconfigurations = _.filter(this.multidatatableconfigurations, (item) => {
				return this.analyticsDataModel.checkComponentForPermission(item);
			});
		}
		if (this.legend) {
			this.legend = JSON.parse(decodeURI(this.legend));
		}
		this.multidatatableconfigurations.forEach((datatableConfig) => {
			datatableConfig.columns = this.dataTableService.generateColumnConfigurationForDataTable(datatableConfig.columns);
		});
		if (!this.nodatamessage || this.nodatamessage === 'undefined' || this.nodatamessage === 'null') {
			this.nodatamessage = 'No Data to Display';
		}
	}

	ngAfterViewInit(): void {
		this.requestData();
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestData(): void {
		this.hasData = true;
		this.showLoad = true;
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
		this.widgetDataSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((response: any) => {
			this.showLoad = false;
			if (response.success && response.data.length > 0) {
				this.hasData = true;
				this.processResponseData(response.data);
			} else {
				this.processNoDataForDatatable();
				this.hasData = false;
			}
		});
	}

	processDeviceColor(responseData: any): void {
		const colorArray = [
			this.colorPalette.primaryColor,
			this.colorPalette.secondaryColor,
			this.colorPalette.tertiaryColor,
			'#a8a8a8'
		];
		for (let j = 0; j < this.multichartconfigurations.length; ++j) {
			this.multichartconfigurations[j].chartConfig.series[0].colors = colorArray
				.slice(0, responseData.length)
				.reverse();
		}
		this.deviceCount = 'device-' + responseData.length;
	}

	processResponseData(responseData: any): void {
		if (this.sectiontitle == 'Devices-r10') {
			this.processDeviceColor(responseData);
			const sortedArray = ['Desktop', 'Mobile', 'Tablet', 'Other'];
			const sorter = (a, b) => {
				return sortedArray.indexOf(b.device) - sortedArray.indexOf(a.device);
			};
			responseData.sort(sorter);
		}
		this.devicesChartData = responseData;
		this.deviceLegendData = [];
		for (const responseObj of responseData) {
			let resultObj: object = {};
			this.multidatatableconfigurations.forEach((datatableConfig) => {
				resultObj = {
					...{},
					...resultObj,
					...this.dataTableService.generateDatatableSourceDataFromConfig(responseObj, datatableConfig.columns)
				};
			});
			this.deviceLegendData.push(resultObj);
		}
		if (this.sectiontitle == 'Devices-r10') this.deviceLegendData = this.deviceLegendData.reverse();
		this.deviceNetworks = this.mapDeviceResponseToInterface(responseData);
	}

	private processNoDataForDatatable(): void {
		this.deviceLegendData = [];
		this.multidatatableconfigurations.forEach((datatableConfig) => {
			const defaultNoData = [];
			for (const deviceType of this.DEFAULT_DEVICES) {
				const resultObj: object = {};
				for (const columnConfig of datatableConfig.columns) {
					resultObj[columnConfig.prop] = deviceType[columnConfig.prop] ? deviceType[columnConfig.prop] : '--';
					if (columnConfig.hasComplexProperty && columnConfig.complexProperty.length > 0) {
						for (const complexProp of columnConfig.complexProperty) {
							resultObj[complexProp.propertyKey] = deviceType[columnConfig.prop] ? deviceType[columnConfig.prop] : '--';
						}
					}
				}
				defaultNoData.push(resultObj);
			}
			this.deviceLegendData =
				this.deviceLegendData.length === 0
					? defaultNoData
					: this.deviceLegendData.map((noDataObj, i) => ({ ...{}, ...noDataObj, ...defaultNoData[i] }));
		});
	}

	public getDeviceColor(device: string): string {
		const legend = this.legend.filter((legendObj) => legendObj.value === device);
		return legend && legend[0] ? legend[0].color : '';
	}

	mapDeviceResponseToInterface(responseArray: any[]): DeviceNetwork[] {
		const deviceNetworks: DeviceNetwork[] = [];
		for (const responseObj of responseArray) {
			const deviceNetwork: DeviceNetwork = {} as DeviceNetwork;
			deviceNetwork.deviceName = !UtilityService.isUndefinedOrNull(responseObj.device)
				? responseObj.device
				: responseObj.name;
			deviceNetwork.impressions = !UtilityService.isUndefinedOrNull(responseObj.impressions)
				? parseInt(responseObj.impressions, 10)
				: 0;
			deviceNetwork.percent25 = !UtilityService.isUndefinedOrNull(responseObj['25_completions_rate'])
				? parseFloat(responseObj['25_completions_rate']) >= 100
					? '100'
					: parseFloat(responseObj['25_completions_rate']).toFixed(2)
				: 0;
			deviceNetwork.value25 = !UtilityService.isUndefinedOrNull(responseObj['25_percent'])
				? parseInt(responseObj['25_percent'], 10)
				: 0;
			deviceNetwork.percent50 = !UtilityService.isUndefinedOrNull(responseObj['50_completions_rate'])
				? parseFloat(responseObj['50_completions_rate']) >= 100
					? '100'
					: parseFloat(responseObj['50_completions_rate']).toFixed(2)
				: 0;
			deviceNetwork.value50 = !UtilityService.isUndefinedOrNull(responseObj['50_percent'])
				? parseInt(responseObj['50_percent'], 10)
				: 0;
			deviceNetwork.percent75 = !UtilityService.isUndefinedOrNull(responseObj['75_completions_rate'])
				? parseFloat(responseObj['75_completions_rate']) >= 100
					? '100'
					: parseFloat(responseObj['75_completions_rate']).toFixed(2)
				: 0;
			deviceNetwork.value75 = !UtilityService.isUndefinedOrNull(responseObj['75_percent'])
				? parseInt(responseObj['75_percent'], 10)
				: 0;
			deviceNetwork.percent100 = !UtilityService.isUndefinedOrNull(responseObj['100_completions_rate'])
				? parseFloat(responseObj['100_completions_rate']) >= 100
					? '100'
					: parseFloat(responseObj['100_completions_rate']).toFixed(2)
				: 0;
			deviceNetwork.value100 = !UtilityService.isUndefinedOrNull(responseObj['100_percent'])
				? parseInt(responseObj['100_percent'], 10)
				: 0;
			deviceNetworks.push(deviceNetwork);
		}
		return deviceNetworks;
	}
}
